import * as THREE from "three";

export class Ship {
  readonly group = new THREE.Group();
  velocity = new THREE.Vector3(0, 0, -28);
  bank = 0; // roll target
  speedBoost = 0;

  // Ship maneuverability. Constraints driving these numbers:
  //   - forward speed -28 u/s + ring Z-spacing 26 u  → ~0.93 s between rings
  //   - ring lateral spread reaches ±10 u at peak difficulty (rings.ts)
  //   - worst case: consecutive rings 20 u apart on the X axis
  // To cover 20 u lateral in 0.93 s the ship needs sustained ~22 u/s plus
  // some accel headroom. Tuned to hit 28 u/s in ~0.4 s.
  private maxLateral = 28;
  private accel = 70;
  private drag = 3.0;

  constructor() {
    // Hull — slim elongated diamond
    const hullGeo = new THREE.ConeGeometry(0.45, 1.6, 6);
    hullGeo.rotateX(Math.PI / 2);
    const hullMat = new THREE.MeshStandardMaterial({
      color: 0x121826,
      metalness: 0.6,
      roughness: 0.25,
      emissive: 0x00e0ff,
      emissiveIntensity: 0.25,
    });
    const hull = new THREE.Mesh(hullGeo, hullMat);
    this.group.add(hull);

    // Wings — two flat triangles
    const wingShape = new THREE.Shape();
    wingShape.moveTo(0, 0);
    wingShape.lineTo(1.2, -0.4);
    wingShape.lineTo(0.2, 0.3);
    wingShape.lineTo(0, 0);
    const wingGeo = new THREE.ShapeGeometry(wingShape);
    const wingMat = new THREE.MeshStandardMaterial({
      color: 0x0a0d14,
      metalness: 0.5,
      roughness: 0.4,
      emissive: 0xff3df0,
      emissiveIntensity: 0.12,
      side: THREE.DoubleSide,
    });
    const wingL = new THREE.Mesh(wingGeo, wingMat);
    wingL.rotation.y = Math.PI / 2;
    wingL.position.set(-0.05, 0, 0.3);
    const wingR = wingL.clone();
    wingR.scale.x = -1;
    this.group.add(wingL, wingR);

    // Glow disc at rear
    const discGeo = new THREE.CircleGeometry(0.5, 32);
    const discMat = new THREE.MeshBasicMaterial({
      color: 0x00e0ff,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const disc = new THREE.Mesh(discGeo, discMat);
    disc.position.z = 0.7;
    disc.rotation.y = Math.PI;
    this.group.add(disc);

    // Halo ring (visual aura)
    const halo = new THREE.Mesh(
      new THREE.RingGeometry(0.62, 0.85, 48),
      new THREE.MeshBasicMaterial({
        color: 0xff3df0,
        transparent: true,
        opacity: 0.35,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    halo.position.z = 0.65;
    this.group.add(halo);
  }

  reset() {
    this.group.position.set(0, 0, 0);
    this.group.rotation.set(0, 0, 0);
    this.velocity.set(0, 0, -28);
    this.bank = 0;
    this.speedBoost = 0;
  }

  update(dt: number, input: THREE.Vector2, boost: boolean) {
    // lateral acceleration
    const ax = input.x * this.accel;
    const ay = input.y * this.accel;
    this.velocity.x += ax * dt;
    this.velocity.y += ay * dt;
    // drag on lateral
    this.velocity.x -= this.velocity.x * this.drag * dt;
    this.velocity.y -= this.velocity.y * this.drag * dt;
    // clamp lateral
    this.velocity.x = THREE.MathUtils.clamp(this.velocity.x, -this.maxLateral, this.maxLateral);
    this.velocity.y = THREE.MathUtils.clamp(this.velocity.y, -this.maxLateral, this.maxLateral);

    // forward speed (negative Z); boost adds
    const targetForward = boost ? -46 : -28;
    this.speedBoost = THREE.MathUtils.damp(this.speedBoost, boost ? 1 : 0, 4, dt);
    this.velocity.z = THREE.MathUtils.damp(this.velocity.z, targetForward, 3, dt);

    // integrate
    this.group.position.addScaledVector(this.velocity, dt);

    // banking visual — roll opposite to lateral input, pitch with vertical
    const targetBank = -input.x * 0.6;
    const targetPitch = input.y * 0.25;
    this.group.rotation.z = THREE.MathUtils.damp(this.group.rotation.z, targetBank, 6, dt);
    this.group.rotation.x = THREE.MathUtils.damp(this.group.rotation.x, targetPitch, 6, dt);
  }

  get position() {
    return this.group.position;
  }
}
